import {
  Application,
  ApplicationStatusId,
  ApplicationStatusText,
} from "src/types/Application";
import { RiskLevelEnum } from "src/types/Applicant";
import { Configuration } from "src/types/Redux";
import { getRiskLevel } from "./Utils";

// Legacy applicationIds can arrive as numeric strings (e.g. "14238") while
// Application.applicationId for the same app is a number; Lease Domain UUID strings pass
// through untouched. Every help-request ingestion path (single fetch, bulk fetch) funnels
// through this, so normalizing here is the one place that keeps the strict ===
// joins against state.applications (applicationsSlice.ts and ApplicationList.tsx) working
// for both id shapes.
export function normalizeApplicationId(
  applicationId: number | string
): number | string {
  return typeof applicationId === "string" && /^\d+$/.test(applicationId)
    ? parseInt(applicationId, 10)
    : applicationId;
}

export function computeReadyToFinish(data: Application) {
  // Lease Domain decides eligibility server side; legacy has no such field and keeps the status rule.
  if (typeof data.canHideFromAssociates === "boolean") {
    return data.canHideFromAssociates ? 1 : 0;
  }

  const applicationStatusId = data.applicationStatusId;
  const cancelled =
    data && applicationStatusId === ApplicationStatusId.Cancelled;

  const completed = data
    ? [
        ApplicationStatusId.LeaseSigned,
        ApplicationStatusId.LeaseExpired,
        ApplicationStatusId.LeaseDeclined,
      ].includes(applicationStatusId)
    : false;

  const readyToFinishCount = cancelled || completed ? 1 : 0;

  return readyToFinishCount;
}

/**
 * Determines whether an application has any applicants who:
 * - Are not soft-deleted
 * - Are in "Screening Returned" status (applicationStatusId === 5)
 * - Have a HIGH risk score based on configuration
 * - Have NOT been cleared via exclusion list further review
 *
 * @param application - The application containing applicants to evaluate
 * @param configuration - Risk configuration used to derive applicant risk level
 * @returns True if at least one applicant meets all unresolved high-risk criteria
 */
export function hasUnresolvedHighRisk(
  application: Application,
  configuration?: Configuration
): boolean {
  return (
    application?.applicants
      ?.filter((applicant) => !applicant.isSoftDelete)
      ?.some((applicant) => {
        // Check if applicant is in screening returned status
        const applicantScreeningReturned =
          applicant.applicationProcess?.applicationStatusId ===
          ApplicationStatusId.CreditAndIDReturned;

        // Check if applicant has a high risk score
        const hasHighRiskScore = getRiskLevel(applicant);
        // Check if there's no valid exclusion clearance
        const noValidClearance = !applicant.exclusionListFurtherReviewClearedBy;
        return (
          applicantScreeningReturned &&
          hasHighRiskScore.risk === RiskLevelEnum.HIGH &&
          noValidClearance
        );
      }) ?? false
  );
}

export const getHighRiskApplicants = (application?: Application) => {
  if (!application) return [];
  return (
    application.applicants?.filter(
      (applicant) =>
        !applicant.isSoftDelete &&
        getRiskLevel(applicant).risk === RiskLevelEnum.HIGH &&
        !applicant.exclusionListFurtherReviewClearedBy
    ) || []
  );
};

/**
 * Determines the actual status to display based on application status and esignStatus from latest lease event.
 * If the latest lease event has esignStatus === "envelope-voided" or "ENVELOPE_VOIDED",
 * and the application status is LEASE_EXPIRED or LEASE_DECLINED, it returns LEASE_VOIDED.
 *
 * @param statusName - The current status name
 * @param application - The application object containing lease events
 * @returns The adjusted status name
 */
export const getActualLeaseStatus = (
  statusName: string,
  application?: Application
): string => {
  if (!application?.lease?.leaseEvents?.length) {
    return statusName;
  }

  const latestLeaseEvent = application.lease.leaseEvents[0];

  const isLeaseExpired =
    statusName === ApplicationStatusText.LEASE_EXPIRED ||
    statusName === "Document Expired";

  const isEnvelopeVoided =
    latestLeaseEvent?.esignStatus?.toLowerCase() === "envelope-voided";

  // ✅ Only override when EXPIRED + VOIDED
  if (isLeaseExpired && isEnvelopeVoided) {
    return ApplicationStatusText.LEASE_VOIDED;
  }

  return statusName;
};
